import { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useInboxStore, type LeadInfo, type Message } from '../../stores/inbox-store';
import { useInboxDrawerStore } from '../../stores/inbox-drawer-store';
import { WhatsAppChat } from './WhatsAppChat';
import { ForwardDialog } from './ForwardDialog';
import { DoorverwijsDialog } from './DoorverwijsDialog';
import { InboxDrawerPanel } from './InboxDrawerPanel';
import { KlantinfoRail } from './KlantinfoRail';
import { EmailBody } from './EmailBody';
import { CallbackPanel } from './CallbackPanel';
import { CustomerLinkerModal } from '../customers/CustomerLinkerModal';
import { AanFieldPicker, type Candidate as AanCandidate } from './AanFieldPicker';
import { AttachmentList, formatFileSize } from './AttachmentList';
import { CallRecordingPlayer } from '../phone/CallRecordingPlayer';
import { CallButton } from '../phone/CallButton';
import { PhoneNumber } from '../phone/PhoneNumber';
import { AssigneePicker } from './AssigneePicker';
import { SnoozeMenu } from './SnoozeMenu';
import { ConfidenceBadge } from './ConfidenceBadge';
import { MeldVerbeterpunt } from '../agent/MeldVerbeterpunt';
import { KlachtAdviesPanel } from './KlachtAdviesPanel';
import { InboxWasserijBeoordeling } from './InboxWasserijBeoordeling';
import { ActieKaart } from './ActieKaart';
import { shouldSkipDraft } from '../../lib/inbox/draft-gates';
import { classicAfhandelen, classicConceptBlokkade } from '../../lib/inbox/classic-safety';
import { leesMailAfdoeningHerstel } from '../../lib/mail-afdoening';
import { effectiveReplyBrand } from '../../lib/inbox/effective-brand';
import { bepaalUitvoerendMerk } from '../../lib/merk';
import type { Brand } from '../../lib/telephony';
import { useTelephonyClient } from '../../hooks/useTelephonyClient';
import clsx from 'clsx';
import { KlantSuggesties } from './KlantSuggesties';
import { LEAD_FUNNEL_STATUSES, LEAD_FUNNEL_GROUPS, LEAD_FUNNEL_GROUP_LABELS } from '../../lib/offerte-status';
import { isKlantBeantwoordNa } from '../../lib/inbox/beantwoord-na';
import { MAX_MAIL_BIJLAGEN_BYTES, MAX_MAIL_BIJLAGEN_LABEL } from '../../lib/factuurconcept';

const STATUS_OPTIONS = [
  { label: 'Nieuw', value: 'nieuw', color: 'bg-blue-100 text-blue-700' },
  { label: 'In behandeling', value: 'in_behandeling', color: 'bg-amber-100 text-amber-700' },
  { label: 'Beantwoord', value: 'beantwoord', color: 'bg-emerald-100 text-emerald-700' },
  { label: 'Afgehandeld', value: 'afgehandeld', color: 'bg-slate-100 text-slate-500' },
];

const SF_BASE_URL = 'https://cinco.my.salesforce.com';

// Afzender-merken voor de "Van"-selector in de composer. Spiegelt de mailboxen
// in api/_lib/gmail.ts (MAILBOXES). Hiermee kan een antwoord uit een ander merk
// verstuurd worden dan waar het gesprek binnenkwam (bv. inkomend op CINCO,
// antwoorden als KLEDA).
const FROM_BRAND_OPTIONS: Array<{ brand: string; email: string; label: string }> = [
  { brand: 'CINCO', email: 'info@cincocleaning.com', label: 'Cinco Cleaning' },
  { brand: 'KLEDA', email: 'info@kleda.nl', label: 'Kleda' },
  { brand: 'SCHOONHUISSHOP', email: 'info@schoonhuisshop.nl', label: 'Schoonhuisshop' },
  { brand: 'SOFASAVER', email: 'info@sofasaver.nl', label: 'SofaSaver' },
];

const REDEN_NIET_GEINTERESEERD_OPTIONS = [
  '',
  'Prijs te hoog',
  'Duurt te lang',
  'Nieuw kopen',
  'Te ver',
  'Concurrent goedkoper',
  'Zelf reinigen',
  'Moeilijk te reinigen (katoen)',
  'Geen reden gegeven',
  'Anders',
];

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const MAX_TOTAL_ATTACHMENT_BYTES = MAX_MAIL_BIJLAGEN_BYTES;


// ─── LOADING SHIMMER ───────────────────────────────────────────

function DraftShimmer() {
  return (
    <div className="px-6 py-3">
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 animate-pulse">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-4 h-4 bg-indigo-200 rounded" />
          <div className="h-4 w-24 bg-indigo-200 rounded" />
        </div>
        <div className="space-y-2">
          <div className="h-3 bg-indigo-100 rounded w-full" />
          <div className="h-3 bg-indigo-100 rounded w-5/6" />
          <div className="h-3 bg-indigo-100 rounded w-4/6" />
        </div>
      </div>
    </div>
  );
}

// ─── PARTNERAANVRAAG-BANNER ───────────────────────────────────

/**
 * Een voorbereide partneraanvraag die niemand ziet wordt nooit verstuurd
 * (Huygen 03-11). Bij versturen aan de klant zet reply.ts de partner in de cc,
 * maar alleen als het goedgekeurde concept op het laatste inkomende bericht
 * staat. Daarom twee knoppen die het los van een antwoord afhandelen
 * (POST /api/inbox/partner-aanvraag, 04-11): alsnog de partnermail sturen, of
 * stempelen dat kantoor hem al zelf doorstuurde (met de dag).
 */
function PartnerAanvraagBanner({ conversationId, aanvraag }: {
  conversationId: string;
  aanvraag: { lead_nummer: string; partner_naam: string; cc_email: string };
}) {
  const vandaag = new Date().toISOString().slice(0, 10);
  const [busy, setBusy] = useState<'verstuur' | 'stempel' | null>(null);
  const [stempelOpen, setStempelOpen] = useState(false);
  const [verstuurdOp, setVerstuurdOp] = useState(vandaag);
  const [fout, setFout] = useState<string | null>(null);
  const [klaar, setKlaar] = useState<string | null>(null);

  const voerUit = async (actie: 'verstuur' | 'stempel') => {
    setBusy(actie); setFout(null);
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Niet ingelogd.');
      const res = await fetch('/api/inbox/partner-aanvraag', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(actie === 'verstuur'
          ? { conversation_id: conversationId, actie }
          : { conversation_id: conversationId, actie, verstuurd_op: verstuurdOp }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Mislukt (${res.status}).`);
      setKlaar(data.samenvatting || 'Afgehandeld.');
      await useInboxStore.getState().openThread(conversationId);
    } catch (err) {
      setFout(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  };

  if (klaar) {
    return (
      <div role="status" className="mb-3 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-2 text-sm text-emerald-800">{klaar}</div>
    );
  }
  return (
    <div role="status" className="mb-3 bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 text-sm text-indigo-800 space-y-2">
      <div>
        Partneraanvraag {aanvraag.lead_nummer} voor {aanvraag.partner_naam} is goedgekeurd maar nog niet aan de partner verstuurd.
        Bij versturen aan de klant gaat {aanvraag.partner_naam} ({aanvraag.cc_email}) automatisch in de cc, met de bijlagen van de klant.
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={busy !== null} onClick={() => voerUit('verstuur')}
          className="px-3 py-1 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-50">
          {busy === 'verstuur' ? 'Versturen…' : 'Alsnog naar partner sturen'}
        </button>
        {!stempelOpen ? (
          <button type="button" disabled={busy !== null} onClick={() => setStempelOpen(true)}
            className="px-3 py-1 rounded-md border border-indigo-300 text-indigo-800 text-xs font-medium hover:bg-indigo-100 disabled:opacity-50">
            Al zelf doorgestuurd
          </button>
        ) : (
          <span className="inline-flex items-center gap-2 text-xs">
            <label htmlFor="partner-verstuurd-op">doorgestuurd op</label>
            <input id="partner-verstuurd-op" type="date" value={verstuurdOp} max={vandaag} onChange={(e) => setVerstuurdOp(e.target.value)}
              className="border border-indigo-300 rounded px-1 py-0.5 text-xs bg-white" />
            <button type="button" disabled={busy !== null || !verstuurdOp} onClick={() => voerUit('stempel')}
              className="px-3 py-1 rounded-md border border-indigo-300 text-indigo-800 font-medium hover:bg-indigo-100 disabled:opacity-50">
              {busy === 'stempel' ? 'Stempelen…' : 'Stempel'}
            </button>
          </span>
        )}
        <span className="text-xs text-indigo-700">Alsnog sturen = de standaard partnermail met aanvraagpagina en knoppen; de klant krijgt niets.</span>
      </div>
      {fout && <div className="text-xs text-red-700">{fout}</div>}
    </div>
  );
}

// ─── LEADS SIDEBAR ────────────────────────────────────────────

function LeadsSidebar({ leads }: { leads: LeadInfo[] }) {
  const { updateLeadField, deleteLead } = useInboxStore();
  const openDrawer = useInboxDrawerStore((s) => s.open);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const handleStatusChange = async (leadId: string, newStatus: string) => {
    setUpdatingId(leadId);
    try {
      await updateLeadField(leadId, { Status: newStatus });
    } catch { /* error logged in store */ }
    setUpdatingId(null);
  };

  const handleRedenChange = async (leadId: string, newReden: string) => {
    setUpdatingId(leadId);
    try {
      await updateLeadField(leadId, { Reden_niet_geintereseerd__c: newReden });
    } catch { /* error logged in store */ }
    setUpdatingId(null);
  };

  const handleDelete = async (leadId: string) => {
    if (!window.confirm('Weet je zeker dat je deze lead wilt verwijderen?')) return;
    setDeletingId(leadId);
    try {
      await deleteLead(leadId);
    } catch { /* error logged in store */ }
    setDeletingId(null);
  };

  return (
    <div className="pt-3 border-t border-slate-100 space-y-3">
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
        Leads{leads.length > 1 ? ` (${leads.length})` : ''}
      </h4>
      {leads.map((lead) => (
        <div key={lead.id} className="bg-slate-50 rounded-lg p-3 space-y-2.5">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => openDrawer('lead', lead.id)}
              className="text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
              title="Offerte bekijken"
            >
              {lead.reference}
            </button>
            <div className="flex items-center gap-2">
              {lead.total > 0 && (
                <span className="text-xs font-semibold text-slate-700">
                  {'\u20AC'}{lead.total.toFixed(2)}
                </span>
              )}
              {leads.length > 1 && (
                <button
                  onClick={() => handleDelete(lead.id)}
                  disabled={deletingId === lead.id}
                  className="text-slate-300 hover:text-red-500 transition-colors"
                  title="Lead verwijderen"
                >
                  {deletingId === lead.id ? (
                    <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                    </svg>
                  )}
                </button>
              )}
            </div>
          </div>

          {lead.products.length > 0 && (
            <div className="space-y-1">
              {lead.products.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 truncate mr-2">
                    {p.quantity > 1 ? `${p.quantity}x ` : ''}{p.name}
                  </span>
                  {p.total > 0 && (
                    <span className="text-slate-500 whitespace-nowrap">{'\u20AC'}{p.total.toFixed(2)}</span>
                  )}
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Status</label>
            <select
              value={lead.status}
              onChange={(e) => handleStatusChange(lead.id, e.target.value)}
              disabled={updatingId === lead.id}
              className={clsx(
                'w-full mt-0.5 text-xs rounded border border-slate-200 bg-white px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500',
                updatingId === lead.id && 'opacity-50'
              )}
            >
              {!LEAD_FUNNEL_STATUSES.some((s) => s.value === lead.status) && lead.status && (
                <option value={lead.status}>{lead.status}</option>
              )}
              {LEAD_FUNNEL_GROUPS.map((group) => (
                <optgroup key={group} label={LEAD_FUNNEL_GROUP_LABELS[group]}>
                  {LEAD_FUNNEL_STATUSES.filter((s) => s.group === group).map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {(lead.status.toLowerCase().includes('niet ge') || lead.redenNietGeintereseerd) && (
            <div>
              <label className="text-[10px] text-slate-400 uppercase tracking-wider">Reden</label>
              <select
                value={lead.redenNietGeintereseerd}
                onChange={(e) => handleRedenChange(lead.id, e.target.value)}
                disabled={updatingId === lead.id}
                className={clsx(
                  'w-full mt-0.5 text-xs rounded border border-slate-200 bg-white px-2 py-1.5 text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500',
                  updatingId === lead.id && 'opacity-50'
                )}
              >
                {REDEN_NIET_GEINTERESEERD_OPTIONS.map(r => (
                  <option key={r} value={r}>{r || '— Geen reden —'}</option>
                ))}
              </select>
            </div>
          )}

          {lead.offerteUrl && (
            <a
              href={lead.offerteUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              Offerte openen
            </a>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── V3: compacte klantkaart ───────────────────────────────────
// Eén regel per offerte en per order; status en reden zitten in de drawer.
// De oude kaart nam met twee dropdowns per lead de halve kolom (Willem 14-09).

function leadStatusLabel(status: string): string {
  return LEAD_FUNNEL_STATUSES.find((s) => s.value === status)?.label ?? status;
}

const leadGesloten = (status: string) => /^closed|niet ge[iï]nteresseerd|duplicaat/i.test(status || '');

function LeadsCompact({ leads }: { leads: LeadInfo[] }) {
  const openDrawer = useInboxDrawerStore((s) => s.open);
  const [toonGesloten, setToonGesloten] = useState(false);
  // Gesloten offertes (Closed - Lost, niet geïnteresseerd) zeggen niets over
  // het gesprek van nu; ze staan achter een klik (Celine 14-09: vier keer
  // Closed - Lost boven de enige order).
  const open = leads.filter((l) => !leadGesloten(l.status));
  const gesloten = leads.filter((l) => leadGesloten(l.status));
  const getoond = toonGesloten ? leads : open;
  return (
    <div className="pt-3 border-t border-slate-100">
      <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
        Offertes{open.length > 1 ? ` (${open.length})` : ''}
      </h4>
      {getoond.length === 0 && <p className="text-xs text-slate-400 py-1">Geen open offerte</p>}
      <div className="divide-y divide-slate-100">
        {getoond.map((lead) => (
          <button
            type="button"
            key={lead.id}
            onClick={() => openDrawer('lead', lead.id)}
            className="w-full text-left py-1.5 flex items-center gap-2 hover:bg-slate-50 rounded px-1 -mx-1"
            title="Offerte openen"
          >
            <span className="text-xs font-medium text-indigo-600 shrink-0">{lead.reference}</span>
            <span className="text-xs text-slate-500 truncate flex-1">
              {leadStatusLabel(lead.status)}{lead.redenNietGeintereseerd ? ` · ${lead.redenNietGeintereseerd}` : ''}
            </span>
            {lead.total > 0 && <span className="text-xs text-slate-700 shrink-0">{'€'}{lead.total.toFixed(2)}</span>}
          </button>
        ))}
      </div>
      {gesloten.length > 0 && (
        <button type="button" onClick={() => setToonGesloten((v) => !v)} className="mt-1 text-[11px] text-slate-400 hover:text-indigo-600">
          {toonGesloten ? 'Verberg gesloten' : `Toon ${gesloten.length} gesloten`}
        </button>
      )}
    </div>
  );
}

// ─── MAIN COMPONENT ────────────────────────────────────────────

/**
 * variant 'classic' = de medewerkersmodus van 14-09 (concept als hulptekst,
 * geen kaarten). variant 'v3' = hetzelfde scherm plus: samenvatting boven het
 * gesprek, 0-3 actiekaarten met Ja/Nee onder het laatste bericht, concept
 * automatisch in het antwoordveld, Opnieuw via de lezer. Versturen is in
 * beide varianten nooit door een AI-oordeel geblokkeerd.
 * Plan: plans/2026-09-14-inbox-v3-v1-scherm-plus-kaarten.md
 */
export function ThreadDetail({ variant = 'classic' }: { variant?: 'classic' | 'v3' } = {}) {
  const v3 = variant === 'v3';
  const drawerOpenItem = useInboxDrawerStore((s) => s.openItem);
  const openDrawer = useInboxDrawerStore((s) => s.open);
  const closeDrawer = useInboxDrawerStore((s) => s.close);
  const isDrawerOpen = drawerOpenItem !== null;
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [replyText, setReplyText] = useState('');
  const [extraContext, setExtraContext] = useState('');
  const [showExtraContext, setShowExtraContext] = useState(false);
  const [toField, setToField] = useState('');
  const [fromBrand, setFromBrand] = useState('');
  const [ccField, setCcField] = useState('');
  const [bccField, setBccField] = useState('');
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [composerSuggestions, setComposerSuggestions] = useState<AanCandidate[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showDoneToast, setShowDoneToast] = useState(false);
  const [actionToast, setActionToast] = useState<string | null>(null);
  const [forwardingMessage, setForwardingMessage] = useState<Message | null>(null);
  const [showDoorverwijs, setShowDoorverwijs] = useState(false);
  const [isEditingSubject, setIsEditingSubject] = useState(false);
  const [editSubjectValue, setEditSubjectValue] = useState('');
  /** V3: Van/Aan/CC uitgeklapt (default één regel). */
  const [composerOpen, setComposerOpen] = useState(false);
  /** V3: oudere berichten ingeklapt; de laatste twee staan open. */
  const [toonOudere, setToonOudere] = useState(false);
  // V3: hoogte van het antwoordveld, te slepen aan de scheidingsbalk tussen
  // antwoord en bericht (Willem 14-09: "afhankelijk van hoe lang de mail is").
  const [conceptHoogte, setConceptHoogte] = useState<number>(() => {
    try { const v = Number(localStorage.getItem('office_inbox_concept_hoogte')); return v >= 80 ? v : 200; } catch { return 200; }
  });
  const sleepStart = useRef<{ y: number; h: number } | null>(null);
  const startSlepen = (e: React.MouseEvent) => {
    e.preventDefault();
    sleepStart.current = { y: e.clientY, h: conceptHoogte };
    const move = (ev: MouseEvent) => {
      if (!sleepStart.current) return;
      const max = Math.max(160, Math.round(window.innerHeight * 0.65));
      const h = Math.min(max, Math.max(80, sleepStart.current.h + (ev.clientY - sleepStart.current.y)));
      setConceptHoogte(h);
    };
    const up = () => {
      window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up);
      sleepStart.current = null;
      setConceptHoogte((h) => { try { localStorage.setItem('office_inbox_concept_hoogte', String(h)); } catch { /* geen opslag */ } return h; });
      document.body.style.cursor = ''; document.body.style.userSelect = '';
    };
    document.body.style.cursor = 'row-resize'; document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', move); window.addEventListener('mouseup', up);
  };
  const subjectInputRef = useRef<HTMLInputElement>(null);

  const {
    activeThread,
    isLoadingThread,
    aiDraft,
    aiDraftAnalysis,
    aiDraftConfidence,
    aiDraftConfidenceReason,
    aiDraftModel,
    aiDraftError,
    aiActiesMessageId,
    aiActies,
    mailVerbModi,
    actieBusyIndex,
    actieError,
    besluitActie,
    isGeneratingDraft,
    isSending,
    sendError,
    sendWarning,
    autoAdvanceEnabled,
    sendReply,
    generateDraft,
    generateKlachtAdvies,
    isGeneratingKlachtAdvies,
    klachtAdviesError,
    klachtAdvies,
    klachtBeoordelingBezig,
    updateStatus,
    updateSubject,
    setActiveThread,
    selectNextThread,
    toggleKlantinfo,
    unlinkCustomerFromConversation,
    refreshCustomerInfo,
    isRefreshingCustomer,
  } = useInboxStore();
  const [isLinkerOpen, setIsLinkerOpen] = useState(false);
  const { dial } = useTelephonyClient();

  const activeId = activeThread?.conversation.id ?? null;
  const [isClosing, setIsClosing] = useState(false);
  const conceptFout = activeThread ? classicConceptBlokkade(activeThread.conversation,
    activeThread.messages, aiDraft, aiActiesMessageId) : null;

  // Phone call data (fetched for phone conversations)
  const [callData, setCallData] = useState<{
    id: string;
    recording_url: string | null;
    duration_seconds: number | null;
    recording_duration_seconds: number | null;
    callback_status: string | null;
    has_voicemail: boolean;
    voicemail_transcription: string | null;
    notes: string | null;
    note_category: string | null;
    from_number: string;
    status: string;
  } | null>(null);
  const [isUpdatingCallback, setIsUpdatingCallback] = useState(false);

  // Auto-focus subject input when editing
  useEffect(() => {
    if (isEditingSubject && subjectInputRef.current) {
      subjectInputRef.current.focus();
      subjectInputRef.current.select();
    }
  }, [isEditingSubject]);

  // Reset per-thread state when active thread changes — voorkomt dat extra context
  // van vorige email meelekt en composer-velden gevuld blijven.
  useEffect(() => {
    setExtraContext('');
    setShowExtraContext(false);
    setReplyText('');
    setToField('');
    setFromBrand('');
    setCcField('');
    setBccField('');
    setShowCcBcc(false);
    setPendingFiles([]);
    setFileError(null);
    setCallData(null);
    setIsEditingSubject(false);
    setComposerSuggestions([]);
    setShowDoorverwijs(false);
    setActionToast(null);
    setComposerOpen(false);
    setToonOudere(false);
  }, [activeId]);

  // V3: het concept staat direct in het antwoordveld; een nieuw concept (na
  // Opnieuw of na een Ja) vervangt de tekst. Hoe minder klikken, hoe beter.
  useEffect(() => {
    if (v3 && aiDraft) setReplyText(aiDraft);
  }, [v3, aiDraft]);

  // Sluit drawer wanneer de actieve thread verandert. Niet bij de eerste
  // thread na mount: dan komt de drawer uit de URL (?open=order:…) en zou hij
  // direct weer dichtgaan (deep-link naar een drawer, rondgang 14-09).
  const vorigeActiveId = useRef<string | null>(null);
  useEffect(() => {
    if (vorigeActiveId.current !== null && vorigeActiveId.current !== activeId) closeDrawer();
    vorigeActiveId.current = activeId;
  }, [activeId, closeDrawer]);

  // Fetch call data for phone conversations
  useEffect(() => {
    if (!activeThread || activeThread.conversation.channel !== 'phone') return;
    let cancelled = false;
    import('../../lib/supabase').then(({ supabase }) => {
      supabase
        .from('calls')
        .select('id, recording_url, duration_seconds, recording_duration_seconds, callback_status, has_voicemail, voicemail_transcription, notes, note_category, from_number, status')
        .eq('conversation_id', activeThread.conversation.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
        .then(({ data }) => {
          if (!cancelled && data) setCallData(data);
        });
    });
    return () => { cancelled = true; };
  }, [activeThread?.conversation.id, activeThread?.conversation.channel]);

  const handleCallbackUpdate = async (newStatus: 'completed' | 'not_needed') => {
    if (!callData) return;
    setIsUpdatingCallback(true);
    try {
      const { supabase } = await import('../../lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      await fetch('/api/telephony/callback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ callId: callData.id, callback_status: newStatus }),
      });
      setCallData(prev => prev ? { ...prev, callback_status: newStatus } : null);
    } catch (err) {
      console.error('[ThreadDetail] callback update error:', err);
    }
    setIsUpdatingCallback(false);
  };

  const handleCallBack = () => {
    if (!callData || !activeThread) return;
    dial(callData.from_number, activeThread.conversation.brand);
  };

  // Pre-fill "Aan" field when thread loads.
  // Priority: effective_sender (resolveert Reply-To vs From server-side bij
  // relay-mails) > conversation.from_email > laatste inkomende from > klant-email.
  useEffect(() => {
    if (!activeThread || toField) return;
    const conv = activeThread.conversation;
    const lastIncoming = [...activeThread.messages].reverse().find(m => m.is_incoming);
    const resolved =
      conv.effective_sender?.email ||
      conv.from_email ||
      lastIncoming?.from_email ||
      activeThread.customer?.customer?.email ||
      '';
    if (resolved) setToField(resolved);
  }, [activeThread]);

  // Pre-fill "Van" met het effectieve merk: normaal het gespreksmerk, maar een
  // generiek CINCO-gesprek met een open KLEDA/SHS/SofaSaver-order verstuurt uit
  // dat órder-merk (consistent met de AI-draft-kennis). Medewerker kan wijzigen.
  useEffect(() => {
    if (!activeThread || fromBrand) return;
    const herkomstMerk = effectiveReplyBrand(
      activeThread.conversation.brand,
      activeThread.customer?.orders,
    ).toUpperCase();
    // Een satellietmerk (VLOERKLEEDREINIGEN, BANKREINIGEN) staat niet in
    // FROM_BRAND_OPTIONS en viel daardoor terug op CINCO: een antwoord aan een
    // vloerkleedreinigen-klant ging dan uit info@cincocleaning.com terwijl al
    // zijn andere mail van Kleda komt. Los het merk op naar de uitvoerder.
    setFromBrand(bepaalUitvoerendMerk({ herkomstMerk }));
  }, [activeThread, fromBrand]);

  // Fetch composer-suggestions (Aan-veld chip-picker kandidaten) per thread.
  useEffect(() => {
    if (!activeId) return;
    let cancelled = false;
    (async () => {
      try {
        const { supabase } = await import('../../lib/supabase');
        const { data: { session } } = await supabase.auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const res = await fetch(`/api/inbox/composer-suggestions?conversation_id=${activeId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data: { candidates?: AanCandidate[] } = await res.json();
        if (!cancelled) setComposerSuggestions(data.candidates || []);
      } catch (err) {
        if (!cancelled) console.error('[ThreadDetail] composer-suggestions error:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [activeId]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    const el = messagesEndRef.current?.parentElement;
    // V3: het te beantwoorden bericht staat bovenaan; niet naar het einde springen.
    if (el) el.scrollTop = v3 ? 0 : el.scrollHeight;
  }, [activeThread?.messages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.max(120, Math.min(el.scrollHeight, 320)) + 'px';
  }, [replyText]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    const next = [...pendingFiles, ...picked];
    const total = next.reduce((s, f) => s + f.size, 0);
    if (total > MAX_TOTAL_ATTACHMENT_BYTES) {
      setFileError(`Bestanden samen ${(total / 1024 / 1024).toFixed(1)} MB, maximaal ${MAX_MAIL_BIJLAGEN_LABEL}. Verklein de foto of stuur hem los.`);
    } else {
      setFileError(null);
    }
    setPendingFiles(next);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (idx: number) => {
    setPendingFiles((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      const total = next.reduce((s, f) => s + f.size, 0);
      if (total <= MAX_TOTAL_ATTACHMENT_BYTES) setFileError(null);
      return next;
    });
  };

  const handleSend = async (tekstOverride?: string) => {
    const tekst = tekstOverride ?? replyText;
    const hasFiles = pendingFiles.length > 0;
    if ((!tekst.trim() && !hasFiles) || isSending || klachtBeoordelingBezig) return;
    if (hasFiles && pendingFiles.reduce((s, f) => s + f.size, 0) > MAX_TOTAL_ATTACHMENT_BYTES) return;
    // V3: het concept is geschreven voor de situatie ná Ja (antwoord en kaart
    // vertellen hetzelfde). Kale Verstuur met open kaarten stuurt dan een
    // belofte zonder uitvoering; vraag dat expliciet.
    if (v3 && !tekstOverride && openKaarten.length > 0
      && !window.confirm(`Er ${openKaarten.length === 1 ? 'staat nog 1 kaart' : `staan nog ${openKaarten.length} kaarten`} open en het antwoord gaat ervan uit dat die is uitgevoerd. Toch versturen zonder uit te voeren? (Gebruik anders "Ja + versturen".)`)) return;

    const html = tekst.replace(/\n/g, '<br/>');
    const opts: { to?: string; cc?: string; bcc?: string; from_brand?: string; handmatig_antwoord: true } = { handmatig_antwoord: true };
    if (toField.trim()) opts.to = toField.trim();
    if (ccField.trim()) opts.cc = ccField.trim();
    if (bccField.trim()) opts.bcc = bccField.trim();
    if (fromBrand) opts.from_brand = fromBrand;

    // Store handles optimistic update + auto-advance
    await sendReply(
      html,
      tekst,
      Object.keys(opts).length > 0 ? opts : undefined,
      hasFiles ? pendingFiles : undefined,
    );
    if (!useInboxStore.getState().sendError) {
      setPendingFiles([]);
      setFileError(null);
      if (autoAdvanceEnabled) {
        setShowDoneToast(true);
        setTimeout(() => setShowDoneToast(false), 1500);
      }
    }
  };

  const handleUseDraft = useCallback(() => {
    if (aiDraft) {
      setReplyText(aiDraft);
    }
  }, [aiDraft]);

  const handleGenerateDraft = () => {
    generateDraft(extraContext.trim() || undefined);
  };

  // V3: Opnieuw neemt de tekst uit het schrijfvak mee als huidig concept (de
  // medewerker heeft er misschien al in geschreven), leegt het instructieveld
  // en zegt dat er een nieuw concept staat (Willem 14-09: "ik krijg geen nieuw
  // concept" terwijl de lezer wel had herschreven).
  const handleOpnieuwV3 = async () => {
    const instructie = extraContext.trim() || undefined;
    await useInboxStore.getState().leesOpnieuw(instructie, replyText.trim() || undefined);
    if (useInboxStore.getState().aiDraftError) return;
    setExtraContext('');
    setActionToast(instructie ? 'Nieuw concept staat in het antwoordveld' : 'Opnieuw gelezen, concept staat in het antwoordveld');
    setTimeout(() => setActionToast(null), 3000);
  };

  const handleGenerateKlachtAdvies = () => {
    generateKlachtAdvies(extraContext.trim() || undefined);
  };

  // V3 "Ja + versturen": alle open kaarten die geen keuze vragen uitvoeren, één
  // keer herlezen zodat het concept de uitkomst (referentie, link) bevat, en
  // versturen. Kaarten met een keuze (dag, klachtoptie), een blokkade, of die
  // apart verzenden (partner, afdoening) blijven handwerk.
  const openKaarten = v3 && aiActies
    ? aiActies.map((a, i) => ({ a, i })).filter(({ a }) => !a.uitkomst && !a.preview?.blokkade && mailVerbModi[a.verb] === 'voorbereiden'
        && !['plan_afspraak', 'verwerk_klachtkeuze', 'sluit_mail_zonder_antwoord', 'bereid_partnerdoorverwijzing', 'bereid_factuurconcept', 'bereid_offerteconcept'].includes(a.verb))
    : [];
  const [doenBezig, setDoenBezig] = useState(false);
  const handleDoenEnVersturen = async () => {
    if (isSending || isGeneratingDraft || doenBezig || openKaarten.length === 0) return;
    setDoenBezig(true);
    try {
      for (const { i } of openKaarten) await besluitActie(i, 'akkoord', undefined, undefined, { geenHerlezing: true });
      const na = useInboxStore.getState();
      const mislukt = (na.aiActies ?? []).some((a) => a.uitkomst?.besluit === 'akkoord' && a.uitkomst?.ok === false);
      if (na.actieError || mislukt) { setActionToast('Een actie is niet gelukt; controleer de kaart voor je verstuurt.'); setTimeout(() => setActionToast(null), 5000); return; }
      await na.leesOpnieuw('De actie is zojuist uitgevoerd. Verwerk de uitkomst (referentie, link) concreet in het antwoord; beloof niets meer wat al gebeurd is.');
      const s2 = useInboxStore.getState();
      const nieuw = s2.aiDraft;
      if (s2.aiDraftError || !nieuw?.trim()) { setActionToast('Concept kon niet worden bijgewerkt; controleer en verstuur zelf.'); setTimeout(() => setActionToast(null), 5000); return; }
      setReplyText(nieuw);
      await handleSend(nieuw);
    } finally {
      setDoenBezig(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      if (v3 && openKaarten.length > 0) void handleDoenEnVersturen();
      else handleSend();
    }
  };

  const handleMarkDone = useCallback(async () => {
    if (!activeThread || activeThread.conversation.status === 'afgehandeld' || isClosing) return;
    setIsClosing(true);
    try {
      await classicAfhandelen(activeThread.conversation);
      await useInboxStore.getState().fetchConversations();
      if (useInboxStore.getState().activeThread?.conversation.id !== activeThread.conversation.id) return;
      if (autoAdvanceEnabled && !v3) void selectNextThread();
      else void setActiveThread(null);
    } catch (error) {
      setActionToast(error instanceof Error ? error.message : 'Afhandelen mislukt.');
    } finally { setIsClosing(false); }
  }, [activeThread, autoAdvanceEnabled, selectNextThread, setActiveThread, isClosing, v3]);

  // Keyboard shortcuts: E = done, I = in behandeling, Cmd/Ctrl+I = klantinfo, Esc = clear active
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!activeThread) return;
      const target = e.target as HTMLElement;

      // Esc: blur input or clear active thread (in-page, list remains visible)
      if (e.key === 'Escape') {
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          (target as HTMLInputElement).blur();
          return;
        }
        e.preventDefault();
        void setActiveThread(null);
        return;
      }

      // Skip when in form fields
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
      // Skip when focus is inside a ThreadList item — list owns its own E/I via selectedIndex
      if (target.closest('[data-conv-item]')) return;

      // Cmd/Ctrl+I = toggle klantinfo slide-out
      if (e.key === 'i' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        toggleKlantinfo();
        return;
      }

      if (e.key === 'e' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (activeThread.conversation.status !== 'afgehandeld') {
          e.preventDefault();
          void handleMarkDone();
        }
      }

      if (e.key === 'i' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (activeThread.conversation.status !== 'in_behandeling') {
          e.preventDefault();
          void updateStatus(activeThread.conversation.id, 'in_behandeling');
        }
      }

      // D = draft in composer laden + focus (zelfde pad als "Gebruiken"-knop);
      // daarna verstuurt Cmd+Enter. Twee toetsen, met een bewuste blik ertussen.
      if (e.key === 'd' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (aiDraft) {
          e.preventDefault();
          handleUseDraft();
          textareaRef.current?.focus();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeThread, setActiveThread, updateStatus, handleMarkDone, toggleKlantinfo, aiDraft, handleUseDraft]);

  if (isLoadingThread) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-3 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!activeThread) {
    return (
      <div className="flex-1 flex items-center justify-center text-slate-400 px-6 text-center">
        <div className="max-w-sm">
          <svg className="w-12 h-12 mx-auto mb-3 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
          </svg>
          <p className="text-sm">Selecteer een gesprek uit de lijst</p>
        </div>
      </div>
    );
  }

  const { conversation, messages, attachments, customer } = activeThread;
  const isWhatsApp = conversation.channel === 'whatsapp';
  // V3: het bericht waarop je antwoordt (de lezer-bron, anders het laatste
  // klantbericht) staat bovenaan; alle andere berichten zitten achter "Toon
  // eerdere berichten". Zo landt het oog op klantvraag → kaart → antwoord.
  const hoofdIdx = v3 ? (() => {
    let i = aiActiesMessageId ? messages.findIndex((m) => m.id === aiActiesMessageId) : -1;
    if (i < 0) for (let k = messages.length - 1; k >= 0; k--) if (messages[k]?.is_incoming) { i = k; break; }
    return i;
  })() : -1;
  const hoofdBericht = hoofdIdx >= 0 ? messages[hoofdIdx] : undefined;
  // Berichten ná de klantvraag (ons antwoord, een tweede klantmail) blijven
  // zichtbaar; alleen wat eraan voorafging klapt in. Voorheen verdween ook het
  // zojuist verzonden antwoord achter "Toon 1 eerdere bericht" (Travelbase 17-09).
  const nieuwerDanHoofd: Message[] = hoofdBericht ? messages.filter((m, i) => i !== hoofdIdx && m.message_date > hoofdBericht.message_date) : [];
  const ouderDanHoofd: Message[] = hoofdBericht ? messages.filter((m, i) => i !== hoofdIdx && !(m.message_date > hoofdBericht.message_date)) : [];
  const weergaveBerichten: Message[] = v3 && hoofdBericht ? [hoofdBericht, ...nieuwerDanHoofd, ...ouderDanHoofd] : messages;
  const zichtbaarGrens = v3 && hoofdBericht ? 1 + nieuwerDanHoofd.length : weergaveBerichten.length;
  const ouderGrens = v3 && hoofdBericht ? ouderDanHoofd.length : 0;

  // V3: samenvatting en kaarten horen bij het bericht waarop de lezer werkte.
  const lezerBron = v3 && aiActiesMessageId ? messages.find((m) => m.id === aiActiesMessageId) : undefined;
  const samenvatting = lezerBron?.ai_samenvatting?.trim() || null;
  // Al geantwoord ná het bericht waar de kaart bij hoort: een onbesliste kaart
  // is dan achterhaald en verdwijnt (Marijke 14-09: "wacht op akkoord" naast
  // ons antwoord "Staat genoteerd"). Besliste kaarten blijven als historie.
  const beantwoordNaKaart = isKlantBeantwoordNa(messages, lezerBron);
  const zichtbareKaarten = v3 && aiActies ? aiActies.map((a, i) => ({ a, i })).filter(({ a }) => a.uitkomst || !beantwoordNaKaart) : [];
  const kaartenBlok = zichtbareKaarten.length > 0 ? (
    <div className="space-y-2 py-2">
      {zichtbareKaarten.map(({ a, i }) => (
        <ActieKaart
          key={`${a.verb}-${i}`}
          actie={a}
          index={i}
          modus={mailVerbModi[a.verb]}
          busy={actieBusyIndex !== null || isGeneratingDraft}
          executing={actieBusyIndex === i}
          compact
          onBesluit={besluitActie}
        />
      ))}
      {actieError && <p role="alert" className="text-xs text-red-600">{actieError}</p>}
    </div>
  ) : null;
  const samenvattingBlok = v3 && samenvatting ? (
    <div className="mb-3 rounded-lg border border-indigo-100 bg-indigo-50 px-4 py-2.5 text-sm text-slate-800">
      <span className="font-medium text-indigo-700">Klant wil:</span> {samenvatting}
    </div>
  ) : null;
  const isPhone = conversation.channel === 'phone';
  const isCallback = conversation.subtype === 'callback';

  // With the reviewed-disposition capability, automated mail is read too.
  // A subject/sender gate never decides that the conversation is handled.
  const lastIncomingForGate = [...messages].reverse().find((m) => m.is_incoming);
  const afdoeningHerstel = leesMailAfdoeningHerstel(lastIncomingForGate?.ai_afdoening_herstel);
  const draftSkipReason = activeThread.afdoening_beschikbaar || afdoeningHerstel ? null : shouldSkipDraft(conversation, lastIncomingForGate ?? null);

  // Subtiele header-actie naast "Toewijzen" — staat er voor élk thread-type
  // (e-mail/telefoon/terugbel/WhatsApp), dus altijd vindbaar op één plek.
  const doorverwijsButton = (
    <button
      type="button"
      onClick={() => setShowDoorverwijs(true)}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors whitespace-nowrap"
      title="Doorsturen naar partner — reply + registratie"
    >
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l4-4m0 0l4 4M7 4v12a4 4 0 004 4h10" />
      </svg>
      Naar partner
    </button>
  );

  return (
    <div className="flex-1 flex overflow-hidden min-w-0">
      {/* Done toast */}
      {showDoneToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2">
          <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Afgehandeld
        </div>
      )}

      {/* Action toast (bv. doorverwijzing geregistreerd) */}
      {actionToast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2">
          <svg className="w-4 h-4 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          {actionToast}
        </div>
      )}

      {/* Main thread area */}
      {isWhatsApp ? (
        <div
          className={clsx(
            'flex flex-col min-w-0 overflow-hidden',
            isDrawerOpen ? 'basis-[40%] flex-shrink-0' : 'flex-1'
          )}
        >
          {/* Header — WhatsApp */}
          <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center gap-4">
            <button
              onClick={() => void setActiveThread(null)}
              className="text-slate-400 hover:text-slate-600 transition-colors"
              title="Sluit gesprek"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <svg className="w-5 h-5 text-emerald-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            <div className="flex-1 min-w-0">
              <h2 className="text-sm font-semibold text-slate-900 truncate">
                {conversation.customer_name || conversation.from_name || 'WhatsApp'}
              </h2>
              <p className="text-xs text-slate-500">
                {conversation.phone ? (
                  <PhoneNumber number={conversation.phone} brand={conversation.brand as Brand} sourceContext="inbox-thread" className="hover:text-slate-700" />
                ) : (
                  conversation.from_email
                )} — {conversation.brand}
              </p>
            </div>
            <SnoozeMenu conversationId={conversation.id} snoozedUntil={conversation.snoozed_until} />
            {doorverwijsButton}
            <AssigneePicker conversation={conversation} />
            <div className="flex items-center gap-1">
              {STATUS_OPTIONS.map((opt) => {
                const isActive = conversation.status === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => opt.value === 'afgehandeld' ? handleMarkDone() : updateStatus(conversation.id, opt.value)}
                    disabled={opt.value === 'afgehandeld' && (isClosing)}
                    className={clsx(
                      'text-xs font-medium px-2.5 py-1 rounded-md transition-colors',
                      isActive
                        ? opt.color
                        : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                    )}
                  >
                    {opt.value === 'afgehandeld' ? (
                      <span className="inline-flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {opt.label}
                      </span>
                    ) : opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          {(samenvattingBlok || kaartenBlok) && (
            <div className="px-6 pt-3 flex-shrink-0">
              {samenvattingBlok}
              {kaartenBlok}
            </div>
          )}
          <WhatsAppChat key={conversation.id} conversation={conversation} messages={messages} attachments={attachments}
            conceptBlokkade={v3 ? null : conceptFout}
            afhandelGeblokkeerd={isClosing} onAfhandelen={handleMarkDone} />
        </div>
      ) : (
      <div
        className={clsx(
          'flex flex-col min-w-0 min-h-0 overflow-hidden',
          isDrawerOpen ? 'basis-[40%] flex-shrink-0' : 'flex-1'
        )}
      >
        {/* Header — Email */}
        <div className="px-6 py-3 border-b border-slate-200 bg-white flex items-center gap-4 flex-shrink-0">
          <button
            onClick={() => void setActiveThread(null)}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            title="Sluit gesprek"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex-1 min-w-0">
            {isEditingSubject ? (
              <input
                ref={subjectInputRef}
                type="text"
                value={editSubjectValue}
                onChange={(e) => setEditSubjectValue(e.target.value)}
                onBlur={() => {
                  const trimmed = editSubjectValue.trim();
                  if (trimmed && trimmed !== conversation.subject) {
                    updateSubject(conversation.id, trimmed);
                  }
                  setIsEditingSubject(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                  if (e.key === 'Escape') {
                    setEditSubjectValue(conversation.subject || '');
                    setIsEditingSubject(false);
                  }
                }}
                className="text-sm font-semibold text-slate-900 w-full border-b border-indigo-400 outline-none bg-transparent py-0.5"
              />
            ) : (
              <h2
                className="text-sm font-semibold text-slate-900 truncate cursor-pointer hover:text-indigo-600 transition-colors"
                onClick={() => {
                  setEditSubjectValue(conversation.subject || '');
                  setIsEditingSubject(true);
                }}
                title="Klik om onderwerp te bewerken"
              >
                {conversation.subject || '(Geen onderwerp)'}
              </h2>
            )}
            <p className="text-xs text-slate-500">
              {conversation.from_name || conversation.from_email} — {conversation.brand}
            </p>
          </div>
          {conversation.phone && (
            <CallButton
              number={conversation.phone}
              brand={conversation.brand as Brand | undefined}
              customerName={conversation.customer_name || conversation.from_name || undefined}
              sourceContext="inbox-thread"
              variant="inline"
            >
              <span aria-hidden>📞</span>
              <span>Bel</span>
            </CallButton>
          )}
          <SnoozeMenu conversationId={conversation.id} snoozedUntil={conversation.snoozed_until} />
          {doorverwijsButton}
          <AssigneePicker conversation={conversation} />
          {v3 ? (
            // V3: één statuskeuze plus Afhandelen; vier losse knoppen pasten niet
            // naast een open drawer (header brak op drie regels, rondgang 14-09).
            <div className="flex items-center gap-1.5 shrink-0">
              <select
                value={conversation.status}
                onChange={(e) => { if (e.target.value === 'afgehandeld') void handleMarkDone(); else void updateStatus(conversation.id, e.target.value); }}
                className={clsx('text-xs font-medium px-2 py-1 rounded-md border-0 cursor-pointer', STATUS_OPTIONS.find((o) => o.value === conversation.status)?.color ?? 'bg-slate-100 text-slate-600')}
                title="Status"
              >
                {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
              </select>
              {conversation.status !== 'afgehandeld' && (
                <button
                  onClick={() => void handleMarkDone()}
                  disabled={isClosing}
                  className="text-xs font-medium px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 hover:bg-emerald-100 hover:text-emerald-700 disabled:opacity-50"
                  title="Afhandelen (E)"
                >
                  ✓ Afhandelen
                </button>
              )}
            </div>
          ) : (
          <div className="flex items-center gap-1">
            {STATUS_OPTIONS.map((opt) => {
              const isActive = conversation.status === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => opt.value === 'afgehandeld' ? handleMarkDone() : updateStatus(conversation.id, opt.value)}
                  disabled={opt.value === 'afgehandeld' && (isClosing)}
                  className={clsx(
                    'text-xs font-medium px-2.5 py-1 rounded-md transition-colors',
                    isActive
                      ? opt.color
                      : 'text-slate-400 hover:bg-slate-100 hover:text-slate-600'
                  )}
                >
                  {opt.value === 'afgehandeld' ? (
                    <span className="inline-flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {opt.label}
                    </span>
                  ) : opt.label}
                </button>
              );
            })}
          </div>
          )}
        </div>

        {/* V3: samenvatting als vaste regel; concept en kaarten staan boven het gesprek (order-1/2), het gesprek eronder (order-3). */}
        {v3 && samenvattingBlok && <div className="px-6 pt-3 flex-shrink-0 order-1"><div className="max-w-4xl mx-auto">{samenvattingBlok}</div></div>}

        {/* Messages */}
        <div className={clsx('flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4', v3 && 'order-3 border-t border-slate-200 [&>*]:max-w-4xl [&>*]:mx-auto')}>
          {/* Phone call info card */}
          {isPhone && callData && (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  callData.status === 'missed' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-600'
                )}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">
                    {callData.status === 'missed' ? 'Gemist gesprek' : 'Telefoongesprek'}
                    {callData.duration_seconds ? ` — ${Math.floor(callData.duration_seconds / 60)}:${(callData.duration_seconds % 60).toString().padStart(2, '0')}` : ''}
                  </p>
                  <p className="text-xs text-slate-500"><PhoneNumber number={callData.from_number} brand={conversation.brand as Brand} sourceContext="inbox-call" /></p>
                </div>
                {callData.callback_status === 'pending' && (
                  <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700 font-medium">Terugbellen</span>
                )}
                {callData.callback_status === 'completed' && (
                  <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-medium">Teruggebeld</span>
                )}
                {callData.callback_status === 'not_needed' && (
                  <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-500 font-medium">Niet nodig</span>
                )}
              </div>

              {callData.notes && (
                <div className="text-sm text-slate-600 bg-white rounded p-2 border border-slate-100">
                  {callData.note_category && (
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-wide mr-2">{callData.note_category}</span>
                  )}
                  {callData.notes}
                </div>
              )}

              {callData.recording_url && (
                <CallRecordingPlayer
                  callId={callData.id}
                  durationSeconds={callData.recording_duration_seconds || callData.duration_seconds || undefined}
                />
              )}

              {callData.has_voicemail && callData.voicemail_transcription && (
                <div className="text-sm text-slate-600 bg-white rounded p-2 border border-slate-100">
                  <span className="text-xs font-medium text-slate-400 uppercase tracking-wide block mb-1">Voicemail</span>
                  {callData.voicemail_transcription}
                </div>
              )}

              {callData.status === 'missed' && callData.callback_status !== 'completed' && callData.callback_status !== 'not_needed' && (
                <div className="flex items-center gap-2 pt-1">
                  <button
                    onClick={handleCallBack}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium rounded-md transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                    Terugbellen
                  </button>
                  <button
                    onClick={() => handleCallbackUpdate('not_needed')}
                    disabled={isUpdatingCallback}
                    className="px-3 py-1.5 text-xs font-medium text-slate-500 hover:bg-slate-100 rounded-md transition-colors"
                  >
                    Niet nodig
                  </button>
                </div>
              )}
            </div>
          )}

          {!v3 && samenvattingBlok}


          {weergaveBerichten.map((msg, idx) => (v3 && idx >= zichtbaarGrens && !toonOudere) ? null : (
            <div
              key={msg.id}
              className={clsx(
                'transition-opacity',
                v3 ? 'w-full px-1 py-3 border-b border-slate-100' : 'rounded-lg p-4 max-w-[85%]',
                v3
                  ? (msg.is_incoming ? '' : 'bg-slate-50/70 rounded-lg px-4')
                  : msg.is_incoming
                    ? 'bg-white border border-slate-200'
                    : 'bg-indigo-50 border border-indigo-100 ml-auto',
                msg._optimisticStatus === 'sending' && 'opacity-60'
              )}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-slate-900">
                  {msg.from_name || msg.from_email}
                </span>
                <span className="text-xs text-slate-400">
                  {formatDate(msg.message_date)}
                </span>
                {msg._optimisticStatus === 'sending' && (
                  <span className="text-xs text-indigo-500 inline-flex items-center gap-1">
                    <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    versturen…
                  </span>
                )}
                {(msg._optimisticStatus === 'sent' || (v3 && !msg.is_incoming && !msg._optimisticStatus)) && (
                  <span className="text-xs text-emerald-600">verzonden</span>
                )}
                {msg.has_attachments && (
                  <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                  </svg>
                )}
                {!msg._optimisticStatus && msg.id && !msg.id.startsWith('temp_') && (
                  <button
                    type="button"
                    onClick={() => setForwardingMessage(msg)}
                    className="ml-auto text-xs text-slate-400 hover:text-indigo-600 inline-flex items-center gap-1"
                    title="Doorsturen"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 8l4-4m0 0l4 4M7 4v12a4 4 0 004 4h10" />
                    </svg>
                    Doorsturen
                  </button>
                )}
              </div>
              <EmailBody html={msg.body_html} text={msg.body_text} />
              <AttachmentList attachments={attachments.filter(a => a.message_id === msg.id)} />
            </div>
          ))}

          {v3 && ouderGrens > 0 && (
            <button type="button" onClick={() => setToonOudere((v) => !v)} className="block w-full text-left text-xs text-indigo-600 hover:underline">
              {toonOudere ? 'Verberg eerdere berichten' : `Toon ${ouderGrens} eerdere bericht${ouderGrens === 1 ? '' : 'en'}`}
            </button>
          )}

          {/* Concept overgeslagen, niet afgehandeld — zelfde gate als backend */}
          {!isCallback && draftSkipReason && (
            <div className="py-2 flex items-center gap-2 text-sm text-slate-400">
              <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              AI-concept overgeslagen ({draftSkipReason}). Controleer of opvolging nodig is.
            </div>
          )}

          {/* AI Draft section — only for normal threads, not callback subtype */}
          {!isCallback && !draftSkipReason && !aiDraft && !isGeneratingDraft && !aiDraftError && (messages.some(m => m.is_incoming) || conversation.from_email) && (
            <div className="py-2 flex items-center gap-3">
              <button
                onClick={handleGenerateDraft}
                className="text-sm text-indigo-600 hover:text-indigo-700 flex items-center gap-1.5"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                </svg>
                {v3 ? 'Concept maken' : 'AI concept genereren'}
              </button>
              {!v3 && (
              <button
                onClick={handleGenerateKlachtAdvies}
                disabled={isGeneratingKlachtAdvies}
                className="text-sm text-amber-700 hover:text-amber-900 flex items-center gap-1.5 disabled:opacity-50"
                title="Tactisch klacht-advies + concept (Opus, met foto's)"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                Klacht-advies
              </button>
              )}
              {!showExtraContext && (
                <button
                  onClick={() => setShowExtraContext(true)}
                  className="text-xs text-slate-400 hover:text-slate-600"
                >
                  + context toevoegen
                </button>
              )}
              {showExtraContext && (
                <input
                  type="text"
                  value={extraContext}
                  onChange={(e) => setExtraContext(e.target.value)}
                  placeholder="bv. Offerte net verstuurd, klant wil wijzigen..."
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleGenerateDraft(); }}
                />
              )}
            </div>
          )}

          {!isCallback && isGeneratingDraft && <DraftShimmer />}

          {!isCallback && aiDraftError && !v3 && (
            <div className="py-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
                <p className="text-sm text-red-700">{aiDraftError}</p>
                <button
                  onClick={handleGenerateDraft}
                  className="text-sm font-medium text-red-600 hover:text-red-700 ml-3 flex-shrink-0"
                >
                  Opnieuw
                </button>
              </div>
            </div>
          )}

          {!isCallback && isGeneratingKlachtAdvies && (
            <div className="py-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700 flex items-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Klacht-advies genereren (dit kan even duren, foto's worden meegewogen)...
              </div>
            </div>
          )}

          {!isCallback && klachtAdviesError && (
            <div className="py-2">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
                <p className="text-sm text-red-700">{klachtAdviesError}</p>
                <button
                  onClick={handleGenerateKlachtAdvies}
                  className="text-sm font-medium text-red-600 hover:text-red-700 ml-3 flex-shrink-0"
                >
                  Opnieuw
                </button>
              </div>
            </div>
          )}

          {/* Intern klacht-advies (leest zelf uit de store; toont niets als er geen advies is) */}
          {!isCallback && !v3 && activeThread.conversation.channel === 'email' && <InboxWasserijBeoordeling />}
          {!isCallback && !v3 && <KlachtAdviesPanel />}

          {activeThread.conversation.channel === 'email' && activeThread.messages.filter(m => m.is_incoming && (!aiActiesMessageId || m.id === aiActiesMessageId)).slice(-1).map(m => (
            !v3 && <div className="py-2" key={m.id}><MeldVerbeterpunt kanaal="mail" bronId={m.id} /></div>
          ))}


          {!isCallback && !v3 && aiDraft && (
            <div className="py-3">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" />
                    </svg>
                    <span role="status" className="text-sm font-medium text-indigo-700">
                      AI-concept
                    </span>
                    <ConfidenceBadge confidence={aiDraftConfidence} reason={aiDraftConfidenceReason} model={aiDraftModel} diagnosticOnly />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleUseDraft}
                      disabled={isSending}
                      className="text-sm font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-100 px-3 py-1 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Overnemen en zelf controleren (D)"
                    >
                      Gebruiken
                    </button>
                  </div>
                </div>
                <p className="text-xs mb-2 text-slate-600">
                  Controleer de tekst. Er worden geen voorgestelde acties uitgevoerd.
                </p>
                {aiDraftAnalysis && (
                  <p className="text-xs text-indigo-500 mb-2 italic">AI begrijpt: {aiDraftAnalysis}</p>
                )}
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{aiDraft}</p>
                <div className="mt-3 pt-3 border-t border-indigo-200 flex items-center gap-2">
                  <input
                    type="text"
                    value={extraContext}
                    onChange={(e) => setExtraContext(e.target.value)}
                    placeholder="Context toevoegen, bv. Dit is geen klacht, klant wil nieuwe aanvraag..."
                    className="flex-1 text-sm border border-indigo-200 bg-white rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleGenerateDraft(); }}
                  />
                  <button
                    onClick={handleGenerateDraft}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-100 px-3 py-1.5 rounded-lg flex items-center gap-1"
                  >
                    Opnieuw
                  </button>
                  {!klachtAdvies && (
                    <button
                      onClick={handleGenerateKlachtAdvies}
                      disabled={isGeneratingKlachtAdvies}
                      className="text-sm font-medium text-amber-700 hover:text-amber-900 bg-amber-100 px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-50"
                      title="Tactisch klacht-advies + concept (Opus, met foto's)"
                    >
                      Klacht-advies
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* V3: de kaarten staan vast boven het antwoordveld, niet in de scrolllijst.
            Een medewerker moet Ja/Nee zien zonder te scrollen (rondgang 14-09). */}
        {kaartenBlok && (
          <div className={clsx('px-6 pt-2 bg-slate-50 flex-shrink-0 max-h-[40vh] overflow-y-auto', v3 ? 'order-1 border-b border-slate-200' : 'border-t border-slate-200')}>
            <div className={clsx(v3 && 'max-w-4xl mx-auto')}>{kaartenBlok}</div>
          </div>
        )}

        {/* Footer: CallbackPanel for subtype='callback', composer otherwise.
            "Doorsturen naar partner" zit in de header (naast Toewijzen) voor alle types. */}
        {isCallback ? (
          <div className={clsx(v3 && 'order-2')}><CallbackPanel /></div>
        ) : (
          <div className={clsx('px-6 py-4 bg-white flex-shrink-0', v3 ? 'order-2 max-w-4xl w-full mx-auto' : 'border-t border-slate-200')}>
            {conversation.partner_aanvraag?.status === 'voorbereid' && conversation.channel === 'email' && (
              <PartnerAanvraagBanner conversationId={conversation.id} aanvraag={conversation.partner_aanvraag} />
            )}
            {sendWarning && (
              <div role="status" className="mb-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm text-amber-800 flex items-start gap-3">
                <span className="flex-1">{sendWarning}</span>
                <button type="button" onClick={() => useInboxStore.setState({ sendWarning: null })} className="underline">Sluiten</button>
              </div>
            )}
            {sendError && (
              <div className="mb-3 bg-red-50 border border-red-200 rounded-lg px-4 py-2 text-sm text-red-700">
                {sendError}
              </div>
            )}

            {v3 && !composerOpen ? (
              // V3: Van/Aan als één regel; pas uitklappen als je iets wilt wijzigen.
              <div className="mb-2 text-xs text-slate-500 flex items-center gap-2 min-w-0">
                <span className="truncate">
                  Van {(FROM_BRAND_OPTIONS.find((o) => o.brand === fromBrand)?.label ?? FROM_BRAND_OPTIONS.find((o) => o.brand === (conversation.brand || '').toUpperCase())?.label ?? conversation.brand)}
                  {' · '}Aan {toField.trim() || conversation.effective_sender?.email || [...messages].reverse().find((m) => m.is_incoming)?.from_email || conversation.from_email}
                  {ccField.trim() ? ` · CC ${ccField.trim()}` : ''}
                </span>
                <button type="button" onClick={() => setComposerOpen(true)} className="shrink-0 text-indigo-600 hover:underline">wijzig</button>
              </div>
            ) : (
            <div className="space-y-1.5 mb-2">
              {/* "Van" — kies vanuit welk merk/adres dit antwoord verstuurd wordt.
                  Default = merk waar het gesprek binnenkwam; wijzigbaar per antwoord. */}
              <div className="flex items-center gap-2">
                <label className="text-xs text-slate-500 w-8">Van</label>
                <select
                  value={fromBrand}
                  onChange={(e) => setFromBrand(e.target.value)}
                  className="flex-1 text-sm border border-slate-200 rounded-md px-2.5 py-1.5 text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  title="Kies vanuit welk merk je antwoordt"
                >
                  {FROM_BRAND_OPTIONS.map((o) => (
                    <option key={o.brand} value={o.brand}>
                      {o.label} — {o.email}
                    </option>
                  ))}
                </select>
              </div>
              {fromBrand && conversation.brand && fromBrand !== conversation.brand.toUpperCase() && (
                <p className="pl-10 text-[11px] text-amber-600">
                  Verstuurd als {FROM_BRAND_OPTIONS.find((o) => o.brand === fromBrand)?.label ?? fromBrand} — start een aparte thread in die mailbox.
                </p>
              )}
              <AanFieldPicker
                candidates={composerSuggestions}
                value={toField}
                onChange={setToField}
                showCcBccButton={!showCcBcc}
                onShowCcBcc={() => setShowCcBcc(true)}
              />
              {showCcBcc && (
                <>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500 w-8">CC</label>
                    <input
                      type="text"
                      value={ccField}
                      onChange={(e) => setCcField(e.target.value)}
                      placeholder="email@voorbeeld.nl"
                      className="flex-1 text-sm border border-slate-200 rounded-md px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500 w-8">BCC</label>
                    <input
                      type="text"
                      value={bccField}
                      onChange={(e) => setBccField(e.target.value)}
                      placeholder="email@voorbeeld.nl"
                      className="flex-1 text-sm border border-slate-200 rounded-md px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    />
                  </div>
                </>
              )}
            </div>
            )}

            {pendingFiles.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {pendingFiles.map((f, i) => (
                  <span
                    key={`${f.name}-${i}`}
                    className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs text-slate-700"
                  >
                    <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                    </svg>
                    <span className="truncate max-w-[160px]">{f.name}</span>
                    <span className="text-slate-400">{formatFileSize(f.size)}</span>
                    <button
                      type="button"
                      onClick={() => removePendingFile(i)}
                      className="text-slate-400 hover:text-red-600"
                      title="Verwijder bijlage"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
            {fileError && (
              <p className="mb-2 text-xs text-red-600">{fileError}</p>
            )}

            <textarea
              ref={textareaRef}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Typ je antwoord..."
              style={v3 ? { height: `${conceptHoogte}px` } : { minHeight: '120px', maxHeight: '320px' }}
              className={clsx('w-full border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent', v3 ? 'resize-none' : 'resize-y')}
            />
            {/* V3: het concept staat al in het antwoordveld; hier alleen Opnieuw met instructie en het Waarom. */}
            {aiDraftError && (
              <p role="alert" className="mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5">Lezen mislukt: {aiDraftError}</p>
            )}
            {!isCallback && v3 && aiDraft && (
              <div className="py-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={extraContext}
                    onChange={(e) => setExtraContext(e.target.value)}
                    placeholder="Instructie voor Opnieuw, bv. 'korter' of 'geen klacht, klant wil opnieuw laten reinigen'"
                    className="flex-1 text-sm border border-slate-200 bg-white rounded-lg px-3 py-1.5 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                    disabled={isGeneratingDraft}
                    onKeyDown={(e) => { if (e.key === 'Enter') void handleOpnieuwV3(); }}
                  />
                  <button
                    onClick={() => void handleOpnieuwV3()}
                    disabled={isGeneratingDraft || actieBusyIndex !== null}
                    className="text-sm font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-100 px-3 py-1.5 rounded-lg disabled:opacity-50"
                  >
                    {isGeneratingDraft ? 'Lezen...' : 'Opnieuw'}
                  </button>
                </div>
                {(aiDraftAnalysis || (aiActies && aiActies.some((a) => a.reden)) || (lezerBron?.ai_ongedekt?.length ?? 0) > 0) && (
                  <details className="mt-1.5 text-xs text-slate-500">
                    <summary className="cursor-pointer select-none">Waarom</summary>
                    <div className="mt-1 space-y-0.5">
                      {aiDraftAnalysis && <p>{aiDraftAnalysis}</p>}
                      {aiActies?.filter((a) => a.reden).map((a, i) => <p key={i}>{a.verb}: {a.reden}</p>)}
                      {(lezerBron?.ai_ongedekt?.length ?? 0) > 0 && <p>Geen verb voor: {lezerBron!.ai_ongedekt!.join(', ')}</p>}
                      {lezerBron && <div className="pt-1"><MeldVerbeterpunt kanaal="mail" bronId={lezerBron.id} /></div>}
                    </div>
                  </details>
                )}
              </div>
            )}
            {!v3 && <p className="mt-2 text-xs text-slate-500">Controleer je antwoord voor je verstuurt. Acties voer je apart uit in Inbox V2.</p>}
            {v3 && isGeneratingDraft && <p role="status" className="mt-2 text-xs text-indigo-600">De lezer leest het bericht en schrijft een concept…</p>}
            {conceptFout && (!v3 || replyText.trim()) && <p role="status" className={clsx('mt-1 text-xs', v3 ? 'text-slate-500' : 'text-amber-700')}>AI-waarschuwing: {conceptFout} Je kunt de tekst aanpassen en zelf versturen.</p>}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-xs font-medium"
                  title="Bestand toevoegen"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                  </svg>
                  Bijlage
                </button>
                <span className="text-xs text-slate-400">
                  {v3 ? <>{'\u2318'}+Enter versturen · E afhandelen</> : <>D draft — {'\u2318'}+Enter versturen — E afhandelen — I in behandeling — {'\u2318'}+I klantinfo — Esc sluiten</>}
                </span>
              </div>
              {v3 && openKaarten.length > 0 && replyText.trim() !== '' && (
                <button
                  onClick={() => void handleDoenEnVersturen()}
                  disabled={isSending || isGeneratingDraft || doenBezig || !!fileError}
                  title={`${openKaarten.length} kaart${openKaarten.length === 1 ? '' : 'en'} uitvoeren, concept bijwerken en versturen (⌘+Enter)`}
                  className="mr-2 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  {doenBezig ? 'Bezig…' : `Ja${openKaarten.length > 1 ? ` (${openKaarten.length})` : ''} + versturen`}
                </button>
              )}
              <button
                onClick={() => handleSend()}
                disabled={(!replyText.trim() && pendingFiles.length === 0) || isSending || klachtBeoordelingBezig || !!fileError}
                title="Verstuur het door jou gecontroleerde antwoord"
                className={clsx(
                  'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  (replyText.trim() || pendingFiles.length > 0) && !isSending && !fileError
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                )}
              >
                {isSending ? 'Versturen...' : 'Verstuur'}
              </button>
            </div>
          </div>
        )}
        {/* V3: sleepbalk tussen antwoord en bericht; verandert de hoogte van het antwoordveld. */}
        {v3 && !isCallback && (
          <div
            role="separator"
            aria-orientation="horizontal"
            title="Sleep om het antwoordveld groter of kleiner te maken"
            onMouseDown={startSlepen}
            onDoubleClick={() => { setConceptHoogte(200); try { localStorage.setItem('office_inbox_concept_hoogte', '200'); } catch { /* geen opslag */ } }}
            className="order-2 flex-shrink-0 h-2 -my-1 relative z-10 cursor-row-resize group"
          >
            <div className="absolute left-1/2 -translate-x-1/2 top-0.5 w-10 h-1 rounded-full bg-slate-300 group-hover:bg-indigo-400" />
          </div>
        )}
      </div>
      )}

      {/* Klantinfo rail (collapsed) — wisselt tussen items zonder drawer te sluiten */}
      {isDrawerOpen && customer && (
        <KlantinfoRail customer={customer} activeId={drawerOpenItem?.id} />
      )}

      {/* Drawer panel (inline order/offerte view) */}
      {isDrawerOpen && <InboxDrawerPanel />}

      {/* Customer context sidebar (uitgeklapt, alleen als drawer dicht) */}
      {!isDrawerOpen && (
        <div className="w-80 border-l border-slate-200 bg-white overflow-y-auto flex-shrink-0">
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Klantinfo</h3>
              {/* Vers uit SF na een out-of-band mutatie (bv. net een nieuwe
                  opdracht aangemaakt); reset de composer/AI-draft niet */}
              <button
                type="button"
                onClick={() => void refreshCustomerInfo()}
                disabled={isRefreshingCustomer}
                title="Klantinfo en orders opnieuw ophalen"
                className="p-1 rounded text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 disabled:opacity-50 transition-colors"
              >
                <svg
                  className={clsx('w-4 h-4', isRefreshingCustomer && 'animate-spin')}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
              </button>
            </div>

            {/* Risico-thread: dit is de mail waarin we de klant om een keuze
                vroegen over een kleed. Zonder dit signaal wordt het antwoord als
                gewone reply afgehandeld en blijft het kleed liggen. */}
            {conversation.risico?.open && (
              <div className="mb-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-medium mb-1">
                  Risico-thread
                </div>
                <div className="text-sm text-slate-900">
                  De klant beantwoordt hier een risico-melding.
                </div>
                <Link
                  to="/risicos"
                  className="mt-1.5 inline-block text-xs font-medium text-amber-800 hover:underline"
                >
                  Verwerk het antwoord via Risico's
                </Link>
              </div>
            )}

            {/* Echte afzender: het antwoordadres wijkt af van de zichtbare afzender.
                'reply-to' = relay/forward via tussenpersoon; 'conversation' = lead-relay
                die vanaf een no_reply-adres mailt met de klant in de body. In beide
                gevallen het signaal dat het antwoord ergens anders heen gaat. */}
            {(conversation.effective_sender?.source === 'reply-to'
              || conversation.effective_sender?.source === 'conversation') && (
              <div className="mb-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-medium mb-1">
                  Echte afzender
                </div>
                <div className="text-sm text-slate-900 font-medium">
                  {conversation.effective_sender.name || conversation.effective_sender.email}
                </div>
                {conversation.effective_sender.name && (
                  <div className="text-xs text-slate-500 break-all">
                    {conversation.effective_sender.email}
                  </div>
                )}
                <div className="text-[10px] text-amber-700 mt-1.5">
                  Antwoord gaat hierheen — niet naar{' '}
                  {conversation.effective_sender.source === 'conversation'
                    ? ([...messages].reverse().find(m => m.is_incoming)?.from_email
                       || 'het doorstuuradres')
                    : (conversation.from_name || conversation.from_email)}
                </div>
              </div>
            )}

            {customer?.found && customer.customer ? (
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {v3 ? (
                      // V3: klant in de drawer, niet weg uit de inbox (Willem 14-09).
                      <button
                        type="button"
                        onClick={() => openDrawer('klant', customer.customer!.id, { klantType: customer.customer!.type === 'lead' ? 'lead' : 'account' })}
                        className="text-sm font-medium text-slate-900 hover:text-indigo-600 hover:underline text-left"
                        title="Klant openen"
                      >
                        {customer.customer.name}
                      </button>
                    ) : (
                    <Link
                      to={`/klanten/${customer.customer.id}?type=${customer.customer.type}`}
                      className="text-sm font-medium text-slate-900 hover:text-indigo-600 hover:underline"
                    >
                      {customer.customer.name}
                    </Link>
                    )}
                    <span className={clsx(
                      'text-[10px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide',
                      customer.customer.type === 'account'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-amber-100 text-amber-700'
                    )}>
                      {customer.customer.type === 'account' ? 'Klant' : 'Lead'}
                    </span>
                    {customer.customer.blacklisted && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded font-semibold bg-red-100 text-red-700"
                        title={customer.customer.blacklistReden || 'Klant staat op de blacklist'}
                      >
                        ⛔ Blacklist
                      </span>
                    )}
                  </div>
                  {customer.customer.contact && (
                    <p className="text-xs text-slate-600 flex items-center gap-1">
                      <span aria-hidden>👤</span>
                      <span className="font-medium">{customer.customer.contact.name}</span>
                      {customer.customer.contact.title && (
                        <span className="text-slate-400">— {customer.customer.contact.title}</span>
                      )}
                    </p>
                  )}
                  {customer.customer.city && (
                    <p className="text-xs text-slate-500">{customer.customer.city}</p>
                  )}
                  <div className="flex items-center gap-3 mt-1.5">
                    <button
                      type="button"
                      onClick={() => setIsLinkerOpen(true)}
                      className="text-xs text-indigo-600 hover:text-indigo-700"
                    >
                      Wijzig koppeling
                    </button>
                    <button
                      type="button"
                      onClick={() => unlinkCustomerFromConversation(conversation.id)}
                      className="text-xs text-slate-500 hover:text-slate-700"
                    >
                      Ontkoppel
                    </button>
                  </div>
                </div>

                {customer.customer.phone && (
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                    </svg>
                    <PhoneNumber number={customer.customer.phone} brand={conversation.brand as Brand} customerId={customer.customer.id} customerName={customer.customer.name} sourceContext="inbox-klantkaart" className="hover:text-indigo-600" />
                  </div>
                )}

                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  <span className="truncate">{customer.customer.email}</span>
                </div>

                {/* SF is gecut (07-09): in V3 geen link meer naar het archief. */}
                {!v3 && (
                <a
                  href={`${SF_BASE_URL}/${customer.customer.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                  Bekijk in Salesforce
                </a>
                )}

                {customer.leads && customer.leads.length > 0 && (
                  v3 ? <LeadsCompact leads={customer.leads} /> : <LeadsSidebar leads={customer.leads} />
                )}

                {customer.customer.type === 'lead' && customer.customer.leadSource && (
                  <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                    <span className="text-xs text-slate-400">Bron</span>
                    <span className="text-xs font-medium text-slate-600">{customer.customer.leadSource}</span>
                  </div>
                )}

                {v3 && customer.orders.length > 0 && (
                  <div className="pt-3 border-t border-slate-100">
                    <h4 className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">
                      Orders{customer.orders.length > 1 ? ` (${customer.orders.length})` : ''}
                    </h4>
                    <div className="divide-y divide-slate-100">
                      {customer.orders.map((order) => (
                        <button
                          type="button"
                          key={order.id}
                          onClick={() => openDrawer('order', order.id)}
                          className="w-full text-left py-1.5 flex items-center gap-2 hover:bg-slate-50 rounded px-1 -mx-1"
                          title="Order openen"
                        >
                          <span className="text-xs font-medium text-slate-800 shrink-0">{order.reference}</span>
                          <span className="text-xs text-slate-500 truncate flex-1">{order.status}</span>
                          {order.total > 0 && <span className="text-xs text-slate-700 shrink-0">{'€'}{order.total.toFixed(2)}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {!v3 && customer.orders.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                      Orders
                    </h4>
                    <div className="space-y-2">
                      {customer.orders.map((order) => {
                        const conversationContactId = customer.customer?.contact?.id;
                        const isSameContact = !!(order.contact?.id && conversationContactId && order.contact.id === conversationContactId);
                        return (
                          <button
                            type="button"
                            key={order.id}
                            onClick={() => openDrawer('order', order.id)}
                            className={clsx(
                              'block w-full text-left rounded-lg p-3 transition-colors',
                              isSameContact
                                ? 'bg-indigo-50 hover:bg-indigo-100 ring-1 ring-indigo-200'
                                : 'bg-slate-50 hover:bg-slate-100'
                            )}
                            title="Order bekijken"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-slate-700">{order.reference}</span>
                              <span className="text-xs text-slate-500">{order.brand}</span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{order.status}</p>
                            {order.contact?.name && (
                              <p className={clsx(
                                'text-xs mt-1 flex items-center gap-1',
                                isSameContact ? 'text-indigo-700 font-medium' : 'text-slate-500'
                              )}>
                                <span aria-hidden>📍</span>
                                {order.contact.name}
                              </p>
                            )}
                            {order.total > 0 && (
                              <p className="text-xs font-medium text-slate-700 mt-1">
                                {'\u20AC'}{order.total.toFixed(2)}
                              </p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-slate-400">
                <p>Geen klantgegevens gevonden</p>
                <KlantSuggesties conversationId={conversation.id} suggestions={conversation.suggested_customer_ids ?? []} compact />
                {(conversation.phone || conversation.from_email) && (
                  <p className="mt-1 text-xs">{conversation.phone ? (
                    <PhoneNumber number={conversation.phone} brand={conversation.brand as Brand} sourceContext="inbox-thread" className="hover:text-slate-700" />
                  ) : (
                    conversation.from_email
                  )}</p>
                )}
                <button
                  type="button"
                  onClick={() => setIsLinkerOpen(true)}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  <span aria-hidden>🔗</span> Koppel klant
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <CustomerLinkerModal
        open={isLinkerOpen}
        conversationId={conversation.id}
        fromEmail={conversation.from_email || undefined}
        fromName={conversation.from_name || undefined}
        onClose={() => setIsLinkerOpen(false)}
      />

      {forwardingMessage && (
        <ForwardDialog
          message={forwardingMessage}
          originalAttachments={attachments.filter((a) => a.message_id === forwardingMessage.id)}
          onClose={() => setForwardingMessage(null)}
        />
      )}

      {showDoorverwijs && (
        <DoorverwijsDialog
          onClose={() => setShowDoorverwijs(false)}
          onSuccess={(msg) => {
            setActionToast(msg);
            setTimeout(() => setActionToast(null), 4000);
          }}
        />
      )}
    </div>
  );
}
